"use client"

import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts"
import styles from "./PieChart.module.css"

const CustomPieChart = ({
  data = [],
  title,
  subtitle,
  colors = [],
  showLegend = true,
  showTooltip = true,
  showLabels = true,
  showLegendCards = true,
  size = "medium",
  paddingAngle = 0,
  startAngle = 0,
  endAngle = 360,
  cx = "50%",
  cy = "50%",
  legendPosition = "bottom",
  tooltipFormatter,
  animationBegin = 0,
  animationDuration = 800,
  isAnimationActive = true,
  className = "",
  style,
  onMouseEnter,
  onMouseLeave,
  onClick,
  ...props
}) => {
  const defaultColors = [
    "#10b981", // Green - Ready to Distribute
    "#F59E0B", // Orange - Pending Refurbishment
    "#87CEEB", // Light Blue - Needing Warranty
    "#EF4444", // Red
    "#8b5cf6", // Purple
    "#06b6d4", // Cyan
    "#f97316", // Orange
    "#84cc16", // Lime
    "#ec4899", // Pink
    "#6366f1", // Indigo
  ]

  const chartColors = colors.length > 0 ? colors : defaultColors

  const sizeConfig = {
    xs: { width: 200, height: 200, outerRadius: 60 },
    small: { width: 250, height: 250, outerRadius: 80 },
    medium: { width: 350, height: 350, outerRadius: 120 },
    large: { width: 450, height: 450, outerRadius: 160 },
    xl: { width: 550, height: 550, outerRadius: 200 },
  }

  const config = sizeConfig[size] || sizeConfig.medium

  // Calculate total for percentage calculation
  const total = data.reduce((sum, item) => sum + item.value, 0)
  const dataWithTotal = data.map((item) => ({
    ...item,
    total,
  }))

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0]
      const percentage = ((data.value / data.payload.total) * 100).toFixed(0)
      const formattedValue = tooltipFormatter
        ? tooltipFormatter(data.value, data.name, data.payload)
        : `${data.value} (${percentage}%)`

      return (
        <div className={styles.tooltip}>
          <div className={styles.tooltipLabel}>{data.name}</div>
          <div className={styles.tooltipValue}>
            <span className={styles.tooltipColor} style={{ backgroundColor: data.color }} />
            {formattedValue}
          </div>
        </div>
      )
    }
    return null
  }

  const CustomLegend = ({ payload }) => {
    if (!showLegend) return null

    return (
      <div className={styles.legend}>
        {payload.map((entry, index) => (
          <div key={index} className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: entry.color }} />
            <span className={styles.legendText}>{entry.value}</span>
          </div>
        ))}
      </div>
    )
  }

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name, index }) => {
    if (!showLabels) return null

    const RADIAN = Math.PI / 180

    // Calculate the position for the label line end point
    const lineRadius = outerRadius + 20
    const lineX = cx + lineRadius * Math.cos(-midAngle * RADIAN)
    const lineY = cy + lineRadius * Math.sin(-midAngle * RADIAN)

    // Calculate the position for the label text
    const labelRadius = outerRadius + 50
    const labelX = cx + labelRadius * Math.cos(-midAngle * RADIAN)
    const labelY = cy + labelRadius * Math.sin(-midAngle * RADIAN)

    // Determine text anchor based on position
    const textAnchor = labelX > cx ? "start" : "end"

    // Calculate horizontal line length and position
    const horizontalLineLength = 30
    const horizontalLineStartX = lineX
    const horizontalLineEndX = textAnchor === "start" ? lineX + horizontalLineLength : lineX - horizontalLineLength

    const color = chartColors[index % chartColors.length]
    const percentage = (percent * 100).toFixed(0)

    return (
      <g key={`label-${index}`}>
        {/* Diagonal line from pie to horizontal line */}
        <line
          x1={cx + outerRadius * Math.cos(-midAngle * RADIAN)}
          y1={cy + outerRadius * Math.sin(-midAngle * RADIAN)}
          x2={lineX}
          y2={lineY}
          stroke="#9CA3AF"
          strokeWidth={1}
          strokeDasharray="3,3"
        />

        {/* Horizontal line */}
        <line
          x1={horizontalLineStartX}
          y1={lineY}
          x2={horizontalLineEndX}
          y2={lineY}
          stroke="#9CA3AF"
          strokeWidth={1}
          strokeDasharray="3,3"
        />

        {/* Percentage text */}
        <text
          x={horizontalLineEndX + (textAnchor === "start" ? 8 : -8)}
          y={lineY - 8}
          textAnchor={textAnchor}
          dominantBaseline="middle"
          className={styles.labelPercent}
          fill={color}
        >
          {percentage}%
        </text>

        {/* Category name text */}
        <text
          x={horizontalLineEndX + (textAnchor === "start" ? 8 : -8)}
          y={lineY + 8}
          textAnchor={textAnchor}
          dominantBaseline="middle"
          className={styles.labelText}
          fill="#374151"
        >
          {name}
        </text>
      </g>
    )
  }

  const containerClasses = [styles.container, className].filter(Boolean).join(" ")

  return (
    <div className={containerClasses} style={style} {...props}>
    

      <div className={styles.chartAndLegendContainer}>
        <div className={styles.chartContainer}>
          <ResponsiveContainer width="100%" height={config.height}>
            <PieChart margin={{ top: 60, right: 80, bottom: 60, left: 80 }}>
              <Pie
                data={dataWithTotal}
                cx={cx}
                cy={cy}
                innerRadius={0}
                outerRadius={config.outerRadius}
                paddingAngle={paddingAngle}
                startAngle={startAngle}
                endAngle={endAngle}
                dataKey="value"
                animationBegin={animationBegin}
                animationDuration={animationDuration}
                isAnimationActive={isAnimationActive}
                label={renderCustomizedLabel}
                labelLine={false}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onClick={onClick}
              >
                {dataWithTotal.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                ))}
              </Pie>

              {showTooltip && <Tooltip content={<CustomTooltip />} />}

              {showLegend && !showLegendCards && (
                <Legend
                  content={<CustomLegend />}
                  verticalAlign={legendPosition === "bottom" ? "bottom" : "top"}
                  align="center"
                  layout="horizontal"
                  wrapperStyle={{
                    paddingTop: legendPosition === "bottom" ? "20px" : "0",
                    paddingBottom: legendPosition === "top" ? "20px" : "0",
                  }}
                />
              )}
            </PieChart>
          </ResponsiveContainer>
        </div>

        {showLegendCards && (
          <div className={styles.legendCards}>
            {dataWithTotal.map((item, index) => (
              <div key={index} className={styles.legendCard}>
                {/* <div className={styles.legendCardValue}>{item.value}</div> */}
               <div className={styles.legendCardLabel}>
  <div
    className={styles.legendCardDot}
    style={{ backgroundColor: chartColors[index % chartColors.length] }}
  />

  <div className={styles.legendTextRow}>
    <span className={styles.legendValue}>{item.value}</span>
    <span className={styles.legendName}>{item.name}</span>
  </div>
</div>

              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default CustomPieChart
